// ModeLanding.tsx
import { useMemo, useState } from 'react';
import { Truck, Droplets, Gauge } from 'lucide-react';
import Dropdown from '../../components/common/dropdown/dropdown';
import { generateMonthYearOptions, getHoustonDateInfo } from '../../helper_functions';

export type ModeId = 'crude' | 'vac' | 'ngl';

interface ModeConfig {
    id: ModeId;
    label: string;
    description: string;
    icon: React.ReactNode;
    disabled?: boolean;
    comingSoon?: boolean;
}

const MODES: ModeConfig[] = [
    {
        id: 'crude',
        label: 'Crude Transport',
        description: 'Tickets, pricing, and financial data for crude oil transport',
        icon: <Truck size={36} />,
    },
    {
        id: 'vac',
        label: 'Vac Truck',
        description: 'Schedule, OI receipts, AP/AR, and invoicing for vac truck operations',
        icon: <Droplets size={36} />,
    },
    {
        id: 'ngl',
        label: 'NGL',
        description: 'NGL transport and financial data',
        icon: <Gauge size={36} />,
    },
];

interface ModeLandingProps {
    onEnterMode: (mode: ModeId, month: string) => void;
}

const ModeLanding: React.FC<ModeLandingProps> = ({ onEnterMode }) => {
    const monthYearOptions = useMemo(() => generateMonthYearOptions(), []);

    const currentMonthYear = useMemo(() => {
        const { currentMonthName, currentYearNumber } = getHoustonDateInfo();
        return `${currentMonthName} ${currentYearNumber}`;
    }, []);

    const [selectedMonths, setSelectedMonths] = useState<Record<ModeId, string>>({
        crude: currentMonthYear,
        vac: currentMonthYear,
        ngl: currentMonthYear,
    });

    const handleMonthChange = (mode: ModeId, value: string) => {
        setSelectedMonths(prev => ({ ...prev, [mode]: value }));
    };

    return (
        <div className="modeLanding-container">
            <div className="modeLanding-header">
                <h2 className="modeLanding-title">Ticket Data</h2>
                <p className="modeLanding-subtitle">Select a product type to get started</p>
            </div>

            <div className="modeLanding-cards">
                {MODES.map(mode => (
                    <div
                        key={mode.id}
                        className={`modeLanding-card ${mode.disabled ? 'modeLanding-card--disabled' : ''}`}
                    >
                        {mode.comingSoon && (
                            <div className="modeLanding-badge">Coming Soon</div>
                        )}

                        <div className="modeLanding-icon">{mode.icon}</div>

                        <div className="modeLanding-cardLabel">{mode.label}</div>
                        <div className="modeLanding-cardDesc">{mode.description}</div>

                        {!mode.disabled && (
                            <>
                                <div className="modeLanding-monthPicker">
                                    <Dropdown
                                        options={monthYearOptions}
                                        value={selectedMonths[mode.id]}
                                        onChange={(val) => handleMonthChange(mode.id, val)}
                                        sortOrder="none"
                                        width={170}
                                    />
                                </div>

                                <button
                                    className="modeLanding-openBtn"
                                    onClick={() => onEnterMode(mode.id, selectedMonths[mode.id])}
                                >
                                    Open →
                                </button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ModeLanding;
